<template>
    <div class="vx-card p-8 pt-0 flex flex-col gap-4 xs:w-full lg:w-1/2">
        <div class="flex flex-col gap-2">
            <div>
                <LabelForm>{{ $t('admin.interactive-maps.name') }}</LabelForm>
                <vs-input class="border-grey-light w-full"
                          type="text"
                          v-model="interactiveMap.name"
                          :danger="interactiveMap.name == ''"
                />
            </div>

            <div>
                <LabelForm>{{ $t('admin.interactive-maps.url') }}</LabelForm>
                <vs-input class="border-grey-light w-full"
                          type="text"
                          v-model="interactiveMap.url"
                />
            </div>

            <div class="flex gap-4">
                <div class="flex-1">
                    <LabelForm>{{ $t('admin.interactive-maps.type') }}</LabelForm>
                    <Combobox
                        v-model="interactiveMap.type"
                        class="mt-2"
                        :options="['2d', '2ds', '3d'].map(mapType => ({key: mapType, text: mapType}))"
                    />
                </div>
                <div class="flex-1">
                    <LabelForm>{{ $t('admin.interactive-maps.timeofyear') }}</LabelForm>
                    <Combobox
                        v-model="interactiveMap.timeofyear"
                        class="mt-2"
                        :options="['winter', 'summer', 'always', 'never'].map(timeofyear => ({key: timeofyear, text: $t(`admin.interactive-maps.timeofyear-${timeofyear}`)}))"
                    />
                </div>
            </div>



            <div>
                <LabelForm>{{ $t('admin.interactive-maps.mapbox-account') }}</LabelForm>
                <Combobox
                    v-model="interactiveMap.mapbox_account_id"
                    class="mt-2"
                    :options="mapboxAccounts.map(mapboxAccount => ({key: mapboxAccount.id, text: mapboxAccount.username}))"
                    :asyncFirstFetchCallback="fetchNewMapboxAccounts"
                    :asyncNextFetchCallback="fetchNextMapboxAccounts"
                />
            </div>

            <div>
                <LabelForm>Cookieyes</LabelForm>
                <Combobox
                    v-model="interactiveMap.cookieyes_account_id"
                    class="mt-2"
                    :options="[
                        { key: 12, text: $t('yes') },
                        { key: 0, text: $t('no') }
                    ]"
                />
            </div>
        </div>

        <div class="flex items-center ml-0 gap-4 mt-5">
            <vs-button :disabled="!isValid" class="rounded-lg xs:w-full sm:w-auto" @click="save">
                {{ $t('resorts.touristic-objects.form.saveButton') }}
            </vs-button>
            <vs-button type="border" class="rounded-lg xs:w-full sm:w-auto text-primary" @click="$router.push({'name': 'admin-interactive-maps'})">
                {{ $t('resorts.touristic-objects.form.cancelButton') }}
            </vs-button>
        </div>
    </div>
</template>

<script>

import store from "@/modules/Shared/Store/store";
import loader from "@/modules/Shared/Mixins/loader";
import InteractiveMapsService from "@/modules/Admin/Services/InteractiveMapsService";
import MapboxAccountsService from "@/modules/Admin/Services/MapboxAccountsService";
import LabelForm from "@/modules/Shared/Components/form/LabelForm.vue";
import Combobox from "@/modules/Shared/Components/Combobox.vue";
import PaginationService from "@/modules/Shared/Services/PaginationService";

export default {
    name: "EditInteractiveMapPage",
    components: {Combobox, LabelForm},
    mixins: [loader],
    data() {
        return {
            mapboxAccountsComboboxPage: 1,
            mapboxAccountsComboboxEnded: false,
            mapboxAccounts: [],
            interactiveMap: {
                uuid: "",
                name: "",
                type: "3d",
                url: "",
                timeofyear: "winter",
                mapbox_account_id: null,
                cookieyes_account_id: 0,
            },
            new: true,
        }
    },

    computed: {
        isValid() {
            return this.interactiveMap.name
        }
    },

    async beforeRouteEnter(to, from, next) {
        try {
            const interactiveMap = await InteractiveMapsService.getInteractiveMap(to.params.uuid);
            const mapboxAccount = interactiveMap.mapbox_account_id
                ? (await MapboxAccountsService.getMapboxAccounts({ q: "id:" + interactiveMap.mapbox_account_id })).data[0]
                : null;

            await store.dispatch('setRouteElement', interactiveMap)
            next(vm => {
                if (mapboxAccount) {
                    vm.mapboxAccounts = [mapboxAccount]
                }

                vm.interactiveMap = {
                    uuid: interactiveMap.uuid,
                    name: interactiveMap.name,
                    type: interactiveMap.type,
                    url: interactiveMap.url,
                    timeofyear: vm.getTimeofyearFromWinterAndSummer(interactiveMap.winter, interactiveMap.summer),
                    mapbox_account_id: interactiveMap.mapbox_account_id,
                    cookieyes_account_id: interactiveMap.cookieyes_account_id,
                }

                vm.new = false
            })
        } catch (error) {
            await store.dispatch('setRouteElement', null)
            next(vm => {
                vm.interactiveMap = {
                    uuid: to.params.uuid,
                    name: "",
                    type: "3d",
                    url: "",
                    timeofyear: "winter",
                    mapbox_account_id: null,
                    cookieyes_account_id: 0,
                }
            })
        }
    },
    methods: {
        getTimeofyearFromWinterAndSummer(hasWinter, hasSummer) {
            if (hasWinter && hasSummer) {
                return "always"
            }

            if (!hasWinter && !hasSummer) {
                return "never"
            }

            return hasWinter ? "winter" : "summer"
        },
        getWinterFromTimeofyear(timeofyear) {
            return timeofyear === "always" || timeofyear === "summer"
        },
        getSummerFromTimeofyear(timeofyear) {
            return timeofyear === "always" || timeofyear === "winter"
        },
        save() {
            this.loadAndNotify(async () => {
                const payload = {
                    ...this.interactiveMap,
                    url: this.interactiveMap ? this.interactiveMap.url : null,
                    winter: this.getWinterFromTimeofyear(this.interactiveMap.timeofyear),
                    summer: this.getSummerFromTimeofyear(this.interactiveMap.timeofyear),
                }

                if (this.new) {
                    await InteractiveMapsService.createInteractiveMap(this.$route.params.uuid, payload);
                } else {
                    await InteractiveMapsService.updateInteractiveMap(this.interactiveMap.uuid, payload);
                }

                await this.$router.push({'name': 'admin-interactive-maps'});
            })
        },

        async fetchMapboxAccounts(filter, page = 1) {
            const response = await MapboxAccountsService.getMapboxAccounts(
                filter ? PaginationService.parseParameters({
                    filters: [ { field: 'username', value: filter } ],
                    per_page: 30,
                }) : {per_page: 30},
                page
            )

            if (response.data.length < 30) {
                this.templatesComboboxEnded = true
            }

            this.mapboxAccounts = [...this.mapboxAccounts, ...response.data]
        },

        fetchNextMapboxAccounts(filter) {
            if (this.mapboxAccountsComboboxEnded) {
                return
            }
            this.mapboxAccountsComboboxPage += 1
            return this.fetchMapboxAccounts(filter, this.mapboxAccountsComboboxPage)
        },

        fetchNewMapboxAccounts(filter) {
            this.mapboxAccounts = []
            this.mapboxAccountsComboboxPage = 1
            return this.fetchMapboxAccounts(filter, this.mapboxAccountsComboboxPage)
        },
    }
};
</script>
